import React from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';

const TimeRangeButtons = ({ 
  periodType, 
  timeRange, 
  setTimeRange, 
  userPlan = 'Free', // Default to Free if not provided
  size = 'medium'
}) => {
  const navigate = useNavigate();
  
  // Button size mappings (keeping your existing code)
  const buttonSizes = {
    tiny: {
      buttonSize: 'small',
      minWidth: { xs: '50px', sm: '60px' },
      height: { xs: '24px', sm: '28px' },
      fontSize: { xs: '0.75rem', sm: '0.8rem' }
    },
    small: {
      buttonSize: 'small',
      minWidth: { xs: '60px', sm: '70px' },
      height: { xs: '28px', sm: '32px' },
      fontSize: { xs: '0.8rem', sm: '0.875rem' }
    },
    medium: {
      buttonSize: 'medium',
      minWidth: { xs: '70px', sm: '80px' },
      height: { xs: '32px', sm: '36px' },
      fontSize: { xs: '0.875rem', sm: '1rem' }
    },
    large: {
      buttonSize: 'large',
      minWidth: { xs: '80px', sm: '90px' },
      height: { xs: '36px', sm: '42px' },
      fontSize: { xs: '1rem', sm: '1.125rem' }
    },
    xlarge: {
      buttonSize: 'large',
      minWidth: { xs: '90px', sm: '100px' },
      height: { xs: '42px', sm: '48px' },
      fontSize: { xs: '1.125rem', sm: '1.25rem' }
    }
  };

  const currentSize = buttonSizes[size] || buttonSizes.medium;
  
  // Check if a time range is available for user's plan
  const isRangeAvailable = (range) => {
    if (userPlan === 'Premium') return true;
    if (userPlan === 'Basic' && range !== 'MAX') return true;
    if (userPlan === 'Free' && ['1Y', '3Y', '5Y'].includes(range)) return true;
    return false;
  };
  
  // Handle button click with redirect for locked features
  const handleTimeRangeClick = (range) => {
    if (isRangeAvailable(range)) {
      setTimeRange(range);
    } else {
      navigate('/pricing'); // Redirect to pricing page
    }
  };

  // Style for locked buttons
  const getButtonStyle = (range) => {
    const available = isRangeAvailable(range);
    return {
      minWidth: currentSize.minWidth,
      height: currentSize.height,
      fontSize: currentSize.fontSize,
      px: { xs: 1, sm: 2 },
      position: 'relative',
      // Apply grayed out style to locked buttons
      opacity: available ? 1 : 0.6,
      color: available ? undefined : 'text.disabled',
      borderColor: available ? undefined : 'action.disabled',
      '&:hover': {
        // Maintain disabled appearance even on hover for locked buttons
        opacity: available ? undefined : 0.6,
        borderColor: available ? undefined : 'action.disabled',
        backgroundColor: available ? undefined : 'transparent'
      }
    };
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: { xs: 1, sm: 2 },
        mb: 4,
        flexWrap: 'wrap',
        px: { xs: 1, sm: 2 }
      }}
    >
      {periodType === 'quarter' && (
        <Button
          size={currentSize.buttonSize}
          sx={getButtonStyle('1Y')}
          variant={timeRange === '1Y' ? 'contained' : 'outlined'}
          onClick={() => handleTimeRangeClick('1Y')}
        >
          1Y
          {!isRangeAvailable('1Y') && (
            <LockIcon sx={{ 
              position: 'absolute', 
              top: '3px', 
              right: '3px',
              fontSize: '0.75rem'
            }} />
          )}
        </Button>
      )}
      <Button
        size={currentSize.buttonSize}
        sx={getButtonStyle('3Y')}
        variant={timeRange === '3Y' ? 'contained' : 'outlined'}
        onClick={() => handleTimeRangeClick('3Y')}
      >
        3Y
        {!isRangeAvailable('3Y') && (
          <LockIcon sx={{ 
            position: 'absolute', 
            top: '3px', 
            right: '3px',
            fontSize: '0.75rem'
          }} />
        )}
      </Button>
      <Button
        size={currentSize.buttonSize}
        sx={getButtonStyle('5Y')}
        variant={timeRange === '5Y' ? 'contained' : 'outlined'}
        onClick={() => handleTimeRangeClick('5Y')}
      >
        5Y
        {!isRangeAvailable('5Y') && (
          <LockIcon sx={{ 
            position: 'absolute', 
            top: '3px', 
            right: '3px',
            fontSize: '0.75rem'
          }} />
        )}
      </Button>
      
      {/* 10Y Button with conditional lock */}
      <Tooltip title={!isRangeAvailable('10Y') ? "Upgrade to Basic plan to see 10 years of data" : ""}>
        <Button
          size={currentSize.buttonSize}
          sx={getButtonStyle('10Y')}
          variant={timeRange === '10Y' ? 'contained' : 'outlined'}
          onClick={() => handleTimeRangeClick('10Y')}
        >
          10Y
          {!isRangeAvailable('10Y') && (
            <LockIcon sx={{ 
              position: 'absolute', 
              top: '3px', 
              right: '3px',
              fontSize: '0.75rem'
            }} />
          )}
        </Button>
      </Tooltip>
      
      {/* MAX Button with conditional lock */}
      <Tooltip title={!isRangeAvailable('MAX') ? "Upgrade to Premium plan to access all data" : ""}>
        <Button
          size={currentSize.buttonSize}
          sx={getButtonStyle('MAX')}
          variant={timeRange === 'MAX' ? 'contained' : 'outlined'}
          onClick={() => handleTimeRangeClick('MAX')}
        >
          MAX
          {!isRangeAvailable('MAX') && (
            <LockIcon sx={{ 
              position: 'absolute', 
              top: '3px', 
              right: '3px',
              fontSize: '0.75rem'
            }} />
          )}
        </Button>
      </Tooltip>
    </Box>
  );
};

export default TimeRangeButtons;